import {
  Application,
  Loader,
  Point,
  Sprite,
  Text,
  utils,
} from 'pixi.js';
import map from '@/assets/map';
import eventHub from '@/scripts/eventHub';
import audio from '@/scripts/audio';
import store from '@/store';
import { getLang } from '@/scripts/utils';

import BackgroundStage from './stages/BackgroundStage';
import MainStage from './stages/MainStage';
import InteractionManager from './interaction';
import Table from './actors/Table';
import Player from './actors/Player';
import state from './state';
import ticker from './ticker';
import { shakeSprite } from './utils';
import Wave from './actors/Wave';
import Blocks from './emitters/Blocks';
import Enemies from './emitters/Enemies';
import Clouds from './emitters/Clouds';
import Score from './emitters/Score';

class Game {
  constructor() {
    this.app = null;
    this.assets = map;
    this.DOMFrame = null;
    this.DOMCanvas = null;
    this.state = state;

    this.ticker = null;
    this.shareLoaded = false;

    this.backgroundStage = null;
    this.mainStage = null;
  }

  init(DOMFrame, DOMCanvas) {
    this.DOMFrame = DOMFrame;
    this.DOMCanvas = DOMCanvas;

    utils.skipHello();
    this.app = new Application({
      view: this.DOMCanvas,
      autoDensity: false,
      autoStart: false,
      transparent: true,
      // backgroundColor: 0xdbccb3,
      antialias: false,
      sharedLoader: true,
      sharedTicker: false,
    });
    this.ticker = ticker;
    this.ticker.add(() => this.app.render());

    this.resize();

    this.app.render();

    // Event management
    eventHub.$on('tutorial', () => { this.goToTutorial(); });
    eventHub.$on('play', () => { this.startPlaying(); });
    eventHub.$on('pause', () => { this.pause(); });
    eventHub.$on('resume', () => { this.resume(); });
    eventHub.$on('shake', () => { shakeSprite(this.app.stage); });
    eventHub.$on('gameover', () => { this.gameover(); });
    eventHub.$on('replay', () => { this.replay(); });
    eventHub.$on('konami-code', () => { this.applyPostProcessing(); });
  }

  load() {
    // Load assets from map
    const sectionKeys = Object.keys(this.assets);
    sectionKeys.forEach((sectionKey) => {
      const assetKeys = Object.keys(this.assets[sectionKey]);
      assetKeys.forEach((assetKey) => {
        Loader.shared.add(`${sectionKey}/${assetKey}`, this.assets[sectionKey][assetKey].url);
      });
    });
    const assets = new Promise((resolve) => { Loader.shared.load(resolve); });
    // Init and load audio
    audio.init();
    const sound = audio.load();
    // Wait for all promises to be resolved
    return Promise.all([assets, sound]);
  }

  resume() {
    this.ticker.start();
    state.paused = false;
  }

  pause() {
    this.ticker.stop();
    state.paused = true;
  }

  resize() {
    const rect = this.DOMFrame.getBoundingClientRect();
    this.app.renderer.resize(rect.width, rect.height);
  }

  async setUpGame() {
    // Initialize the front stage and the background stage
    this.backgroundStage = new BackgroundStage(this.app.stage);
    this.mainStage = new MainStage(this.app.stage);

    // Init all the actors
    this.table = new Table(this.backgroundStage.sprite);
    this.player = new Player(this.mainStage.sprite);
    this.wave = new Wave(this.mainStage.sprite, this.player, this.app.renderer);

    // Init the emitters
    this.blocks = new Blocks(this.mainStage.sprite, this.player);
    this.enemies = new Enemies(this.mainStage.sprite, this.player);
    this.clouds = new Clouds(this.mainStage.sprite);
    this.score = new Score(this.mainStage.sprite, this.player);

    // Init the interaction manger
    this.interaction = new InteractionManager(this.DOMFrame);

    // this.app.stage.scale = new Point(1.03, 1.03);
    // this.app.stage.position.x -= 10;
    this.app.stage.sortChildren();
    this.app.render();
  }

  goToTutorial() {
    audio.stop('background');
    this.player.placeChar();
  }

  async startPlaying() {
    state.reset();
    this.interaction.start();

    // Perform all task needed to spawn chars and so on
    this.player.getReady(this.interaction);
    this.table.startMove();
    this.wave.startMove();
    this.blocks.startSpawn();
    this.enemies.startSpawn();
    this.clouds.startSpawn();
    this.score.start();

    // Play the background music and other audios
    audio.play('background');
  }

  gameover() {
    // audio.play('gameover');
    // First stop various binders and tickers
    this.interaction.stop();
    this.player.gameover();
    this.table.gameover();
    this.wave.gameover();
    this.blocks.gameover();
    this.enemies.gameover();
    this.clouds.stopSpawn();
    this.score.stop();
  }

  replay() {
    // Reset the game state
    state.reset();

    this.player.replay();
    this.table.startMove();
    this.wave.reset();
    this.wave.startMove();
    this.blocks.startSpawn();
    this.enemies.startSpawn();
    this.score.start();

    this.interaction.start();
  }

  async applyPostProcessing() {
    if (this.app.stage.filters !== null) {
      this.app.stage.filters = null;
    } else {
      const pixelateFilter = import('@pixi/filter-pixelate');
      const crtFilter = import('@pixi/filter-crt');
      await Promise.all([pixelateFilter, crtFilter]).then(([pFilter, cFilter]) => {
        this.app.stage.filters = [
          new pFilter.PixelateFilter(3, 3),
          new cFilter.CRTFilter({
            vignettingAlpha: 0.6,
          }),
        ];
        this.app.render();
      });
    }
  }

  async generateShareImage() {
    // Standalone function for generating share image
    const sharepath = `/assets/share/${getLang()}/share.jpg`;
    const baseImage = Sprite.from(sharepath);
    const p = new Promise((resolve) => {
      if (this.shareLoaded) resolve();
      else {
        baseImage.texture.baseTexture.on('loaded', resolve);
        this.shareLoaded = true;
      }
    });
    await p;
    const text = new Text(store.state.mode === 'gameover' ? state.score.toString() : store.state.userData.score, {
      fill: 0xEDF44F,
      fontSize: 140,
      fontFamily: 'Gucci sans',
      fontWeight: 700,
    });
    text.anchor = new Point(0.5, 0.5);
    text.position.x = baseImage.width / 2;
    text.position.y = 630;
    baseImage.addChild(text);
    const b64 = this.app.renderer.plugins.extract.base64(baseImage, 'image/jpg', 0.6);
    return b64;
  }
}

export default Game;
